// Fetches a contact from the host ATS API (GET /api/v1/contacts/:id) — the
// canonical source, used instead of the flaky agentforge profile endpoint.
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { http } from '@/lib/http';

export interface HostContact {
  id: number | string;
  first_name?: string;
  last_name?: string;
  position?: string;
  image_url?: string;
  avatar?: string;
  emails?: { email?: string; primary?: boolean }[];
  local_phone_number?: string;
  phone_code?: string;
  full_address?: string;
  location?: string;
  ats_candidate_source?: { id?: number; name?: string } | null;
  credited_to?: { id?: number; name?: string; avatar?: string } | null;
  experiences?: {
    company?: { name?: string; logo?: string };
    title?: string;
    start_date?: string;
    end_date?: string | null;
  }[];
  educations?: {
    academic_institution?: { name?: string; logo?: string };
    academic_degree?: { name?: string };
    start_date?: string;
    end_date?: string | null;
  }[];
  skills?: string[];
  social?: {
    linkedin_url?: string;
    twitter_url?: string;
    github_url?: string;
  } | null;
}

const hostContactKey = (contactId: string | number) =>
  ['agentforge', 'host-contact', String(contactId)] as const;

const fetchHostContact = async (
  contactId: string | number
): Promise<HostContact | null> => {
  const body = (await http.get(`/contacts/${contactId}`)) as {
    data?: { contact?: HostContact };
    contact?: HostContact;
  };
  return body?.data?.contact ?? body?.contact ?? null;
};

export const useHostContactQuery = (
  contactId: string | number | null,
  enabled: boolean
): UseQueryResult<HostContact | null, Error> =>
  useQuery({
    queryKey: hostContactKey(contactId ?? ''),
    enabled: enabled && contactId !== null && String(contactId).length > 0,
    queryFn: () => fetchHostContact(contactId as string | number),
    staleTime: 60_000,
  });
